"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import "../AutoVerkaufen.css";

import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";
import { TopSectionAutoVerkaufen } from "../sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";
import höchstenPreis from "@/assets/auto-verkaufen/wir-kaufen-dein-auto-verkauf.avif";
import vonZuhauseAus from "@/assets/auto-verkaufen/Verkauft-handy.avif";
import { fragenSteiermark } from "../data";
import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";

// Only needed after step 11; keep auth-related code/CSS out of initial paint.
// Below-the-fold section; defer to reduce initial route JS/CSS.
const Ratgeber = dynamic(() => import("@/components/Ratgeber/Ratgeber"), {
  ssr: false,
});

const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function Steiermark() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionAutoVerkaufen
        title={<>Auto in der Steiermark verkaufen: Tipps & Infos</>}
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung für die
            Steiermark.
          </>
        }
        buttonTitle="Bewertung ansehen"
        linkname="Steiermark"
        link="/auto-verkaufen/steiermark"
        customNav="Auto verkaufen"
        customNavLink="/auto-verkaufen"
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen/steiermark"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color AV-below-fold">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Du möchtest dein Auto in der Steiermark verkaufen?"
              leftText={
                <>
                  <p>
                    Wenn du dein Auto in der Steiermark verkaufen möchtest,
                    merkst du schnell, dass der Prozess aufwendig sein kann. Die
                    Nachfrage nach Gebrauchtwagen ist groß, besonders in Graz
                    und den umliegenden Städten. Beliebt sind Klein- und
                    Mittelklassewagen für Pendler, aber auch SUVs und Kombis,
                    die im ländlichen Raum praktisch sind. Viele Fahrzeuge in
                    der Steiermark haben durch Berg- und Landstraßenfahrten
                    einen höheren Verschleiß oder höhere Kilometerstände.
                  </p>
                  <p>
                    Inserate, Preisverhandlungen und das Risiko unseriöser
                    Angebote machen den Verkauf zusätzlich anstrengend. Mit
                    Autoeinfachlos verkaufst du dein Auto in der Steiermark
                    unkompliziert, schnell und fair.
                  </p>
                </>
              }
              rightFirstTitle="Auto verkaufen in der Steiermark – worauf kommt es an?"
              rightFirstText={
                <>
                  <p>
                    Der steirische Markt für Gebrauchtwagen ist vielseitig. In
                    Graz und den Ballungsräumen sind sparsame Stadtwagen
                    gefragt, während im Murtal, im Ausseerland oder in der
                    Südsteiermark eher robuste Fahrzeuge für längere Strecken
                    beliebt sind.
                  </p>

                  <p>
                    <strong>
                      Typische Probleme beim Auto verkaufen in der Steiermark:
                    </strong>
                  </p>
                  <ul className="custom-unordered-list">
                    <li>aufwendige Inserate mit vielen Anfragen</li>
                    <li>Unsicherheit bei der Preisfindung</li>
                    <li>unzuverlässige Käufer oder Zahlungsverzögerungen</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos ersparst du dir diese Schwierigkeiten.
                    Wir präsentieren dein Auto direkt in unserem
                    Händlernetzwerk.
                  </p>

                  <h3>Dein Vorteil mit Autoeinfachlos in der Steiermark</h3>
                  <p>Mit Autoeinfachlos verkaufst du dein Auto ohne Stress:</p>
                  <ul className="custom-unordered-list">
                    <li>
                      Zugang zu über 5.000 Händlern, auch in der Steiermark
                      vertreten
                    </li>
                    <li>
                      Auktion mit mehreren Bietern, dadurch erzielst du
                      automatisch den besten Preis
                    </li>
                    <li>
                      Abholung direkt bei dir, egal ob in Graz, Leoben oder im
                      ländlichen Raum
                    </li>
                    <li>sichere und schnelle Bezahlung</li>
                  </ul>
                  <p>
                    So machst du den Autoverkauf transparent, sicher und
                    effizient.
                  </p>
                </>
              }
              rightSecondTitle="Autoankauf in der Steiermark – privat oder Händler"
              rightSecondText={
                <>
                  <p>
                    Ein Privatverkauf klingt auf den ersten Blick attraktiv.
                    Doch beim Auto verkaufen privat in der Steiermark zeigen
                    sich schnell Nachteile:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>viele Termine und Probefahrten</li>
                    <li>unseriöse Anfragen oder Absagen</li>
                    <li>Risiko von Preisverhandlungen bis zum Schluss</li>
                  </ul>

                  <p>
                    Der Autoankauf über unser Händlernetzwerk bietet klare
                    Vorteile:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>geprüfte Käufer</li>
                    <li>schnelle Abwicklung</li>
                    <li>faire Preise und sichere Zahlung</li>
                  </ul>

                  <p>
                    Mit Autoeinfachlos kombinierst du die Reichweite vieler
                    Interessenten mit der Professionalität geprüfter Händler.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={höchstenPreis}
                h2
                isBig
                fontSize="36px"
                title="Was ist dein Auto in der Steiermark wert?"
                alt="Auto in der Steiermark bewerten"
                text={
                  <>
                    <p>Der Preis für dein Auto hängt von mehreren Faktoren ab:</p>
                    <ul className="custom-unordered-list">
                      <li>Marke und Modell</li>
                      <li>Baujahr und Kilometerstand</li>
                      <li>allgemeiner Zustand, inklusive eventueller Schäden</li>
                      <li>regionale Nachfrage in der Steiermark</li>
                    </ul>
                    <p>
                      Bei Autoeinfachlos kannst du dein Auto kostenlos bewerten
                      lassen. So erhältst du sofort eine Einschätzung und kannst
                      direkt in die Auktion starten.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt Auto bewerten
                      </Link>
                    </p>

                    <h3>So funktioniert der Verkauf in der Steiermark</h3>
                    <ul className="custom-unordered-list">
                      <li>Auto online bewerten lassen – kostenlos und unverbindlich.</li>
                      <li>
                        Auktion starten – Dein Auto wird in unserem
                        Händlernetzwerk präsentiert.
                      </li>
                      <li>
                        Höchstgebot erhalten – mehrere Händler bieten, du
                        entscheidest.
                      </li>
                      <li>
                        Abholung vor Ort in der Steiermark – Dein Auto wird
                        direkt bei dir in der Steiermark übernommen.
                      </li>
                      <li>Sichere Bezahlung – schnell und unkompliziert.</li>
                    </ul>
                    <p>
                      Damit sparst du Zeit, verhinderst unnötigen Stress und
                      erhältst den besten Preis für dein Fahrzeug.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen zum Autoverkauf in der Steiermark"
              fragen={fragenSteiermark}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto verkaufen in der Steiermark"
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
                fahrgestellnummerPruefen
                kaputtesAuto
                wasistmeinautowert
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={vonZuhauseAus}
                h2
                isBig
                fontSize="36px"
                title="Auto verkaufen in der Steiermark leicht gemacht"
                alt="Auto verkaufen in der Steiermark leicht gemacht"
                text={
                  <>
                    <p>
                      Wenn du dein Auto in der Steiermark verkaufen möchtest,
                      musst du dich nicht mit mühsamen Inseraten oder unsicheren
                      Käufern beschäftigen. Mit Autoeinfachlos profitierst du
                      von einem starken Händlernetzwerk, regionaler Abholung und
                      einer sicheren Bezahlung.
                    </p>
                    <p>Starte jetzt mit der kostenlosen Fahrzeugbewertung.</p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Auto kostenlos bewerten
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-bewerten"
            title="Jetzt Auto kostenlos bewerten und verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default Steiermark;
